import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Clock, CheckCircle, LogOut } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NoTeamsGate } from './ui/NoTeamsGate';
import { useTeamStore } from '../store/teamStore';
import { useAuthStore } from '../store/authStore';
import { CoachInvitation } from './CoachInvitation';
import PageHeader from './layout/PageHeader';
import { useSettings } from '../hooks/useSettings';
import { TeamInformationSection } from './settings/TeamInformationSection';
import { IndividualProfileSettingsSection } from './settings/IndividualProfileSettingsSection';
import { PracticePreferencesSection } from './settings/PracticePreferencesSection';
import { DisplaySettingsSection } from './settings/DisplaySettingsSection';
import { DrillAlertSettingsSection } from './settings/DrillAlertSettingsSection';
import { TennisScoringSettingsSection } from './settings/TennisScoringSettingsSection';
import { FeatureSettingsSection } from './settings/FeatureSettingsSection';
import { TeamDeletionSection } from './settings/TeamDeletionSection';
import { CollapsibleSection } from './settings/CollapsibleSection';
import { SubscriptionCard } from './SubscriptionCard';

const Settings: React.FC = () => {
  const { allTeams, fetchAllTeams } = useTeamStore();
  const { signOut } = useAuthStore();
  const navigate = useNavigate();
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const {
    currentTeam,
    formState,
    isLoading,
    isUpdating,
    lastSaved,
    forceUpdate,
    handleInputChange,
    handleTimeFormatChange,
    handlePlayerSortChange,
    handleToggleChange,
    handleSubmit
  } = useSettings();

  // Load teams on component mount
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoadingTeams(true);
      await fetchAllTeams();
      setIsLoadingTeams(false);
    };

    loadTeams();
  }, [fetchAllTeams]);

  // Format last saved time
  const formatLastSaved = (date: Date | null) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffSeconds < 30) return 'just now';
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Show loading state while teams are being loaded
  if (isLoadingTeams) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading teams...</p>
      </div>
    );
  }

  // Show team setup message only after teams are loaded and there are no teams
  if (allTeams.length === 0) {
    return <NoTeamsGate />;
  }

  // Show loading state for team data
  if (!currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading team data...</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mb-4"></div>
        <p className="text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4">
      <PageHeader 
        title="Settings" 
        icon={<SettingsIcon className="h-6 w-6" />}
        actions={
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
              {isUpdating ? (
                <>
                  <Clock className="w-4 h-4 animate-pulse" />
                  <span>Saving...</span>
                </>
              ) : lastSaved ? (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Saved {formatLastSaved(lastSaved)}</span>
                </>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Log out"
              className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50 active:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Log out</span>
            </button>
          </div>
        }
      />
      
      <div className="max-w-7xl mx-auto pb-2 space-y-8">
        {/* Main Settings */}
        <div className="space-y-4">
          {/* Auto-save Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Save className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800 text-sm font-medium">
                Changes are automatically saved
              </span>
            </div>
            <p className="text-blue-600 text-xs mt-1">
              All settings are saved automatically as you make changes. No need to click save!
            </p>
          </div>

          {/* Team Information and Feature Settings are multi-player roster/team-management
              concerns — hidden for a personal (parent/player) account. Practice Preferences
              and Drill Alert Settings stay: Practices is still a live feature there. */}
          {currentTeam.isPersonal ? (
            <CollapsibleSection title="Profile" defaultExpanded={true}>
              <IndividualProfileSettingsSection team={currentTeam} />
            </CollapsibleSection>
          ) : (
            <CollapsibleSection title="Team Information" defaultExpanded={true}>
              <TeamInformationSection
                formState={formState}
                onInputChange={handleInputChange}
                onSubmit={handleSubmit}
              />
            </CollapsibleSection>
          )}

          <CollapsibleSection title="Practice Preferences" defaultExpanded={false}>
            <PracticePreferencesSection
              formState={formState}
              onInputChange={handleInputChange}
            />
          </CollapsibleSection>

          {/* Display Settings */}
          <CollapsibleSection title="Display Settings" defaultExpanded={false}>
            <DisplaySettingsSection
              formState={formState}
              onTimeFormatChange={handleTimeFormatChange}
              onPlayerSortChange={handlePlayerSortChange}
            />
          </CollapsibleSection>

          {!currentTeam.isPersonal && (
            <CollapsibleSection title="Feature Settings" defaultExpanded={false}>
              <FeatureSettingsSection
                formState={formState}
                sport={currentTeam.sport}
                onToggleChange={handleToggleChange}
              />
            </CollapsibleSection>
          )}

          <CollapsibleSection title="Drill Alert Settings" defaultExpanded={false}>
            <DrillAlertSettingsSection
              forceUpdate={forceUpdate}
            />
          </CollapsibleSection>

          {currentTeam.sport === 'Tennis' && (
            <CollapsibleSection title="Match Scoring" defaultExpanded={false}>
              <TennisScoringSettingsSection />
            </CollapsibleSection>
          )}

          {/* Coach Management Section — not applicable to a personal (parent/player) account */}
          {!currentTeam.isPersonal && (
            <CollapsibleSection title="Coach Management" defaultExpanded={false}>
              <CoachInvitation />
            </CollapsibleSection>
          )}

          {/* Team Deletion Section */}
          <CollapsibleSection title="Danger Zone" defaultExpanded={false}>
            <TeamDeletionSection
              teamName={currentTeam.name}
              teamId={currentTeam.id}
            />
            <div className="mt-6 pt-5 border-t border-red-100">
              <p className="text-sm font-medium text-gray-900 mb-1">Delete your account</p>
              <p className="text-sm text-gray-500 mb-3">
                Permanently delete your Hustli account and all associated data. This cannot be undone.
              </p>
              <Link
                to="/delete-account"
                className="inline-flex items-center px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-medium bg-white hover:bg-red-50 transition"
              >
                Request account deletion
              </Link>
            </div>
          </CollapsibleSection>
        </div>

        {/* Subscription at bottom of Settings – same UI as UpgradeModal */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription</h2>
          <SubscriptionCard />
        </div>
      </div>
    </div>
  );
};

export default Settings; 